import { useSyncExternalStore } from 'react';

/**
 * LIGHT OR DARK, held in one place and persisted.
 *
 * <h2>A store rather than React state in `App`</h2>
 *
 * The mode is read in three kinds of place: by `ThemeProvider`, which is in `App`; by the chart,
 * which reads `theme.palette.mode` through MUI's own context; and by a handful of pure modules
 * that pick a colour by contrast and have no component to be passed a prop by —
 * {@link cycleHandleColors} is the one that forced this. A `useState` in `App` would serve the
 * first two and not the third, and the third is exactly where a stale value shows as an invisible
 * mark on a chart rather than as an error.
 *
 * <p>`useSyncExternalStore` keeps every subscriber on one value in the same render pass. The
 * sibling {@link glassTransparency} store exists for the same reason and this follows its shape
 * deliberately — two preferences with two different mechanisms would be two things to learn.
 *
 * <h2>⚠ THE DEFAULT IS DARK, which changes what an existing reader sees</h2>
 *
 * A `coolWhiteTheme` was hardcoded in `App` and there was no way to leave it. The brief asks for
 * the application to "default cleanly to the professional dark aesthetic", so a reader who has
 * never touched the toggle now opens in dark — including every existing one, on their next load.
 *
 * <p>That is a deliberate, visible change rather than a side effect, and it is one click to
 * reverse. Flip {@link DEFAULT_MODE} to make light the default; nothing else needs to change,
 * because every consumer reads the resolved mode rather than assuming one.
 *
 * <h2>Only two values, and anything else resolves to the default</h2>
 *
 * A stored string is whatever was last written to it, and `localStorage` outlives any code that
 * wrote it — a key left behind by an earlier experiment, or a hand-edited value, must not render
 * an application in a mode that does not exist. There is no 'system' mode: the brief asks for a
 * toggle between two, and a third state that follows the OS makes "why is it light" a question
 * about the reader's machine rather than about the button they pressed.
 */

/** Namespaced like the toolbar's own preference, so it is findable in devtools. */
export const THEME_MODE_KEY = 'workspace_theme_mode';

export const LIGHT = 'light';
export const DARK = 'dark';

/** What a reader who has never touched the toggle gets. See the warning above. */
export const DEFAULT_MODE = DARK;

/** The two legal values. Exported so a test can sweep them rather than retyping the pair. */
export const MODES = Object.freeze([LIGHT, DARK]);

/** Anything that is not one of the two resolves to the default, never to `undefined`. */
export const normaliseMode = (raw) => (MODES.includes(raw) ? raw : DEFAULT_MODE);

const read = () => {
  try {
    return normaliseMode(window.localStorage.getItem(THEME_MODE_KEY));
  } catch (e) {
    // Private-mode Safari throws on access, not only on write. An unreadable preference is the
    // default, which is the same answer a first-time reader gets.
    return DEFAULT_MODE;
  }
};

/**
 * Stamps the mode onto `<html data-theme>`.
 *
 * <h2>Why plain CSS needs to know, when everything else reads the MUI theme</h2>
 *
 * Not every surface in this application is styled through `sx`. The study toolbar's background is
 * a gradient in a stylesheet (`TabPanel-toolbar-gradient.css`), which cannot read a React context
 * — so it painted its pale-blue over the dark Paper underneath and left the whole strip near-white
 * on a charcoal workspace. A stylesheet can only follow an attribute.
 *
 * <p>On `<html>` rather than `<body>`, so a rule can match before React has mounted anything, and
 * `data-theme` rather than a class so it is one attribute with one value instead of two classes
 * that can both be present.
 *
 * <p>NOT `prefers-color-scheme`: the mode here is this application's own setting and has nothing
 * to do with the reader's operating system. A stylesheet keyed on the OS would disagree with the
 * toggle the moment the two differed, which is the worst of both.
 */
const stamp = (mode) => {
  try {
    document.documentElement.setAttribute('data-theme', mode);
  } catch (e) {
    // No DOM — a test importing the store, or a server render. The attribute is a presentation
    // detail and nothing reads it back, so its absence costs the gradient and nothing else.
  }
};

let value = read();
stamp(value);
const listeners = new Set();

/** The mode in force: `'light'` or `'dark'`, never anything else. */
export const themeMode = () => value;

/** True when the dark theme is in force. The question most callers actually have. */
export const isDarkMode = () => value === DARK;

/**
 * Set it, persist it, and tell every subscriber at once.
 *
 * <p>A no-op when unchanged, so a caller that re-sets the same mode does not re-render the whole
 * application — which here means rebuilding the chart's entire option object.
 */
export function setThemeMode(next) {
  const wanted = normaliseMode(next);
  if (wanted === value) return;
  value = wanted;
  stamp(wanted);
  try {
    window.localStorage.setItem(THEME_MODE_KEY, wanted);
  } catch (e) {
    // The mode still applies to this session; only remembering it failed. A reader who cannot
    // persist a preference must still be able to change one.
  }
  listeners.forEach((fn) => fn());
}

/** Flip it. What the toolbar's icon calls. */
export const toggleThemeMode = () => setThemeMode(value === DARK ? LIGHT : DARK);

const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** Re-reads `localStorage`. For tests, which need a known starting point per case. */
export function resetThemeModeForTest() {
  value = read();
  stamp(value);
  listeners.forEach((fn) => fn());
}

/** Subscribe a component to the mode. */
export function useThemeMode() {
  return useSyncExternalStore(subscribe, themeMode, themeMode);
}

export default useThemeMode;
